import { motion } from "framer-motion";
import { ArrowDown, Linkedin, BookOpen, Sparkles } from "lucide-react";
import { LocalizedLink } from "./LocalizedLink";
import { useLanguage } from "../i18n/LanguageProvider";


export const Hero = () => {
  const { t } = useLanguage();
  const handleScroll = (e, id) => {
    e.preventDefault();
    const el = document.querySelector(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative min-h-screen flex items-center justify-center overflow-hidden bg-zinc-900"
    >
      {/* Subtle glow */}
      <div className="hero-glow" />

      <div className="max-w-7xl mx-auto px-6 md:px-12 w-full relative z-10 py-16 md:py-24">
        {/* Two-Column Grid */}
        <div className="grid md:grid-cols-2 gap-10 items-center">
          {/* Left Column - Intro & Actions */}
          <div className="order-2 md:order-1">
            {/* Greeting */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="text-sm text-zinc-400 font-medium"
            >
              {t("hero.greeting")}
            </motion.p>

            {/* Value Proposition */}
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              data-testid="hero-name"
              className="text-4xl sm:text-5xl font-extrabold tracking-tight leading-tight text-white mt-3"
            >
              {t("hero.headline")}
            </motion.h1>

            {/* Bio */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              data-testid="hero-subtext"
              className="text-base text-zinc-500 leading-relaxed max-w-md mt-6"
            >
              {t("hero.bio")}
            </motion.p>

            {/* Connect & Read Row */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="mt-6 flex gap-6"
            >
              <a
                href="https://www.linkedin.com/in/alper-kirmizi"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-linkedin-link"
                className="inline-flex items-center gap-2 text-zinc-400 hover:text-white transition-colors text-sm font-medium"
              >
                <Linkedin size={16} strokeWidth={1.5} />
                LinkedIn
              </a>
              <a
                href="https://medium.com/@alperkirmizi"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-medium-link"
                className="inline-flex items-center gap-2 text-zinc-400 hover:text-white transition-colors text-sm font-medium"
              >
                <BookOpen size={16} strokeWidth={1.5} />
                Medium
              </a>
            </motion.div>

            {/* CTA Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.5 }}
              className="mt-8 flex flex-wrap items-center gap-3"
            >
              <LocalizedLink
                to="/testimonials"
                data-testid="cta-recommendations"
                className="inline-flex items-center gap-2 bg-white text-black hover:bg-zinc-200 px-6 py-3 rounded-full font-medium text-sm transition-colors duration-200"
              >
                {t("hero.ctaRecommendations")}
              </LocalizedLink>
              <LocalizedLink
                to="/ai-resources"
                data-testid="cta-ai-resources"
                className="inline-flex items-center gap-2 bg-transparent text-white border border-zinc-700 hover:border-zinc-500 hover:bg-zinc-800/50 px-6 py-3 rounded-full font-medium text-sm transition-colors duration-200"
              >
                <Sparkles size={16} strokeWidth={1.5} />
                {t("hero.ctaAiResources")}
              </LocalizedLink>
              {/* <LocalizedLink
                to="/projects"
                data-testid="cta-projects"
                className="inline-flex items-center gap-2 bg-transparent text-white border border-zinc-700 hover:border-zinc-500 hover:bg-zinc-800/50 px-6 py-3 rounded-full font-medium text-sm transition-colors duration-200"
              >
                View Projects
              </LocalizedLink> */}
            </motion.div>
          </div>

          {/* Right Column - Portrait & Editorial Typography */}
          {/* No entrance animation here on purpose. This is the largest
              contentful element, and fading it in from zero opacity after a
              delay meant the browser did not count it as painted until the
              animation finished, adding about 1.6s to the measured load. The
              text beside it still animates in. */}
          <div className="relative order-1 md:order-2">
            {/* Image Container */}
            <div className="relative">
              {/* Profile Image with effects */}
              <div className="relative aspect-[4/5] rounded-3xl overflow-hidden">
                {/* Largest-contentful element: pre-cropped to the 4:5 box it
                    renders in, so no pixels are downloaded to be thrown away.
                    Declared widths match the files exactly — a mismatch makes
                    the browser upscale, which is what made this look soft. */}
                <picture>
                  <source
                    type="image/webp"
                    srcSet="/img/portrait-600.webp 600w, /img/portrait-900.webp 900w"
                    sizes="(min-width: 768px) 40vw, 92vw"
                  />
                  <img
                    src="/img/portrait-900.jpg"
                    srcSet="/img/portrait-600.jpg 600w, /img/portrait-900.jpg 900w"
                    sizes="(min-width: 768px) 40vw, 92vw"
                    alt="Alper Kirmizi - Senior Software Engineer"
                    data-testid="hero-profile-image"
                    width={900}
                    height={1125}
                    loading="eager"
                    fetchPriority="high"
                    decoding="async"
                    className="w-full h-full object-cover grayscale-[30%] saturate-[70%]"
                  />
                </picture>
                {/* Dark overlay for contrast */}
                <div className="absolute inset-0 bg-gradient-to-t from-zinc-900/60 via-transparent to-transparent" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2, duration: 0.5 }}
        className="absolute bottom-12 left-1/2 -translate-x-1/2"
      >
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
        >
          <ArrowDown size={18} className="text-zinc-600" strokeWidth={1.5} />
        </motion.div>
      </motion.div>
    </section>
  );
};
